import { eventFromParent } from "@clockwise/client-commons/src/util/event";
import { ExternalLinkButton } from "@clockwise/web-commons/src/ui/external-link-button";
import { getUrlForFeature } from "@clockwise/web-commons/src/util/routes.util";
import { createUUID } from "@clockwise/web-commons/src/util/uuid";
import * as React from "react";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import ReactTooltip from "react-tooltip";
import { updateSchedulingSessionId } from "../../state/actions/feed-scheduler.actions";
import { TrackingEvents, track } from "../../util/analytics.util";
import { logger } from "../../util/logger.util";
import { useProcessMessage } from "../chat/ai-chat/hooks/useProcessMessage";
import { generateRescheduleCQL } from "../event-card/utils/generateCQL";
import { EventSchedulingQueryEventParent } from "../event-scheduling/EventScheduling";

const toolTipId = "tooltip_for_rescheduler_reason";

const aiUrl = getUrlForFeature("ai");

export type RescheduleButtonProps = {
  enableRescheduling: boolean;
  disableReschedulingReason: JSX.Element | null;
  eventParent: Pick<EventSchedulingQueryEventParent, "events">;
  calendarId: string;
  buttonText?: string;
};

const RescheduleButton = ({
  enableRescheduling,
  disableReschedulingReason,
  eventParent,
  calendarId,
  buttonText,
}: RescheduleButtonProps) => {
  const dispatch = useDispatch();
  const { processMessage } = useProcessMessage();

  useEffect(() => {
    track(TrackingEvents.EVENT.SCHEDULING.RESCHEDULE_BUTTON_DISPLAYED, {
      buttonText,
    });
  }, [buttonText]);

  const onClickReschedule = () => {
    const event = eventFromParent(eventParent, calendarId);

    if (!event) {
      logger.error("Unable to find event to reschedule");
      return;
    }

    const schedulingSessionId = createUUID();
    dispatch(updateSchedulingSessionId(schedulingSessionId));

    const title = event.title || "Event";

    void processMessage(
      `Reschedule ${title}`,
      {
        eventMentions: [
          {
            title,
            externalEventId: event.eventKey.externalEventId,
            startTime: event.startTime.dateOrDateTime,
          },
        ],
        personMentions: [],
      },
      generateRescheduleCQL(event.eventKey.externalEventId),
    );

    track(TrackingEvents.SCHEDULING.RESCHEDULER_STARTED, {
      schedulingSessionId,
      externalEventId: event.eventKey.externalEventId,
    });
  };

  return (
    <div className="cw-mt-4" data-for={toolTipId} data-tip>
      <ExternalLinkButton
        cw-id="reschedule-button"
        disabled={!enableRescheduling}
        fullWidth
        onLinkOpen={onClickReschedule}
        url={aiUrl}
        variant="outlined"
      >
        {buttonText || "Reschedule"}
      </ExternalLinkButton>
      <ReactTooltip multiline id={toolTipId} effect="solid" disable={!disableReschedulingReason}>
        {disableReschedulingReason}
      </ReactTooltip>
    </div>
  );
};

export default RescheduleButton;
